import { NodeProps } from "reactflow";
import NodeCard from "../NodeCard";
import {
  FlowModuleItemType,
  VariableItemType,
} from "@packages/flow-core/module";
import RenderInput from "../render/RenderInput";
import styles from "./index.less";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { RenderInputProps } from "../render/RenderInput/types";
import { Button, Form, Input, Segmented, Select, Space } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import PromptEditor from "../Editor/PromptEditor";
import { useFlow } from "../FlowEdit/useFlow";
import Divider from "../Divider";
import RenderOutput from "../render/RenderOutput";
import { useUpdateEffect } from "ahooks";
import {
  FlowNodeInputTypeEnum,
  FlowNodeTypeEnum,
} from "@packages/flow-core/module/node/constants";
import { getKnowledgeFieldsService, KnowledgeField } from "../../services/app";

enum ConditionCompareEnum {
  ISNULL = "ISNULL",
  NONNULL = "NONNULL",
  START_WITH = "START_WITH",
  END_WITH = "END_WITH",
  CONTAIN = "CONTAIN",
  NOT_CONTAIN = "NOT_CONTAIN",
  EQ = "EQ",
  NE = "NE",
  GTE = "GTE",
  LTE = "LTE",
  GT = "GT",
  LT = "LT",
  REGEX = "REGEX",
  NOT_REGEX = "NOT_REGEX",
  EQ_PREFIX = "EQ_PREFIX",
}

const SearchParam = memo(
  ({
    item,
    moduleId,
    selectedKnowledge,
    inputs,
    knowledgeQueries,
  }: RenderInputProps) => {
    const { updateNodeValue, getAllNodes, addModuleInput, systemVariables } =
      useFlow();
    const [form] = Form.useForm();

    useUpdateEffect(() => {
      form.setFieldValue("root", []);
      updateNodeValue(moduleId, item.key, []);
    }, [selectedKnowledge]);

    const variables = useMemo(() => {
      const templateVariables =
        getAllNodes()
          .find((node) => node.data.flowType === FlowNodeTypeEnum.templateParam)
          ?.data?.inputs.map((item) => ({
            key: item.key,
            label: item.label,
            isTemplateVariable: true,
          })) || [];
      const globalVariablesModule: Pick<VariableItemType, "key" | "label">[] =
        getAllNodes()
          .find(
            (node) => node.data.flowType === FlowNodeTypeEnum.globalVariable
          )
          ?.data?.inputs.filter((input) => input.edit)
          .map((item) => ({ key: item.key, label: item.label })) || [];

      const moduleVariablesModule: Pick<VariableItemType, "key" | "label">[] =
        inputs
          ?.filter((input) => input.type === FlowNodeInputTypeEnum.target)
          .map((item) => ({
            key: item.key,
            label: item.description || item.label,
            self: true,
          })) || [];

      return [
        ...templateVariables,
        ...globalVariablesModule,
        ...moduleVariablesModule,
        ...(systemVariables || []),
      ];
    }, [getAllNodes, systemVariables, inputs]);

    const conditions = useMemo(() => {
      return (knowledgeQueries as KnowledgeField[])?.map((item) => ({
        label: item.fieldName,
        value: item.fieldName,
      }));
    }, [inputs]);

    // 搜索条件
    const showParamsRelation = useMemo(() => {
      const param = inputs?.find((input) => input.key === "searchParam")?.value;
      return !!param && param instanceof Array && param.length > 1;
    }, [inputs]);

    const searchParamRelation = useMemo(() => {
      const relation = inputs?.find(
        (input) => input.key === "searchParamRelation"
      );

      if (!relation || !relation?.value) {
        // 不存在关系
        addModuleInput(moduleId, {
          key: "searchParamRelation",
          type: "custom",
          label: "",
          value: "AND",
          edit: true,
        });
      }

      return relation?.value || "AND";
    }, [inputs]);

    const containerRef = useRef<HTMLDivElement>(null);

    return (
      <div ref={containerRef}>
        <div className={styles["search-params-title"]}>
          <span className={styles.label}>条件搜索配置</span>
          {showParamsRelation && (
            <Segmented
              size="small"
              options={["AND", "OR"]}
              defaultValue={searchParamRelation}
              className={styles.relationship}
              onChange={(v) => {
                // 更新条件关系
                updateNodeValue(moduleId, "searchParamRelation", v);
              }}
            />
          )}
        </div>

        {/* 更改判断条件 */}
        <Form
          form={form}
          onValuesChange={(_, values) => {
            updateNodeValue(moduleId, item.key, values.root);
          }}
          initialValues={{ root: item.value }}
          validateTrigger="blur"
        >
          <Form.List name="root">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map(({ name, ...restField }) => {
                    return (
                      <Space
                        key={name}
                        style={{ width: "100%", display: "flex" }}
                        wrap={false}
                        align="baseline"
                        className={
                          styles["advance-knowledgebase-condition-item"]
                        }
                      >
                        <Form.Item {...restField} name={[name, "key"]}>
                          <Select
                            options={conditions}
                            getPopupContainer={(node) =>
                              containerRef.current || node
                            }
                            onChange={() => {
                              form.setFieldValue(
                                ["root", name, "operate"],
                                undefined
                              );
                              form.setFieldValue(
                                ["root", name, "value"],
                                undefined
                              );
                            }}
                          />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const key = getFieldValue(["root", name, "key"]);
                            const matchedField = (
                              knowledgeQueries as KnowledgeField[]
                            ).find((item) => item.fieldName === key);
                            let options: { label: string; value: string }[] =
                              [];
                            if (matchedField?.type === "TEXT") {
                              options = [
                                {
                                  label: "为空",
                                  value: ConditionCompareEnum.ISNULL,
                                },
                                {
                                  label: "不为空",
                                  value: ConditionCompareEnum.NONNULL,
                                },
                                {
                                  label: "开始为",
                                  value: ConditionCompareEnum.START_WITH,
                                },
                                {
                                  label: "结束为",
                                  value: ConditionCompareEnum.END_WITH,
                                },
                                {
                                  label: "包含",
                                  value: ConditionCompareEnum.CONTAIN,
                                },
                                {
                                  label: "不包含",
                                  value: ConditionCompareEnum.NOT_CONTAIN,
                                },
                                {
                                  label: "相等",
                                  value: ConditionCompareEnum.EQ_PREFIX,
                                },
                              ];
                            } else {
                              options = [
                                {
                                  label: "为空",
                                  value: ConditionCompareEnum.ISNULL,
                                },
                                {
                                  label: "不为空",
                                  value: ConditionCompareEnum.NONNULL,
                                },
                                {
                                  label: "等于",
                                  value: ConditionCompareEnum.EQ,
                                },
                                {
                                  label: "不等于",
                                  value: ConditionCompareEnum.NE,
                                },
                                {
                                  label: "大于等于",
                                  value: ConditionCompareEnum.GTE,
                                },
                                {
                                  label: "小于等于",
                                  value: ConditionCompareEnum.LTE,
                                },
                                {
                                  label: "大于",
                                  value: ConditionCompareEnum.GT,
                                },
                                {
                                  label: "小于",
                                  value: ConditionCompareEnum.LT,
                                },
                              ];
                            }
                            return (
                              <Form.Item
                                {...restField}
                                name={[name, "operate"]}
                              >
                                <Select
                                  options={options}
                                  getPopupContainer={(node) =>
                                    containerRef.current || node
                                  }
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const operate = getFieldValue([
                              "root",
                              name,
                              "operate",
                            ]);
                            return (
                              <div style={{ maxWidth: 400 }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "value"]}
                                >
                                  {!(
                                    operate === ConditionCompareEnum.ISNULL ||
                                    operate === ConditionCompareEnum.NONNULL
                                  ) ? (
                                    <PromptEditor
                                      h={32}
                                      variables={variables}
                                      singleLine
                                      showOpenModal={false}
                                      showResize={false}
                                    />
                                  ) : (
                                    (operate === ConditionCompareEnum.ISNULL ||
                                      operate ===
                                        ConditionCompareEnum.NONNULL) && (
                                      <Input disabled />
                                    )
                                  )}
                                </Form.Item>
                              </div>
                            );
                          }}
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    );
                  })}
                  <Form.Item>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          key: undefined,
                          operate: undefined,
                          value: undefined,
                        })
                      }
                    >
                      添加搜索条件
                    </Button>
                  </Form.Item>
                </>
              );
            }}
          </Form.List>
        </Form>
      </div>
    );
  }
);

const VectorSearch = memo(
  ({
    item,
    inputs,
    knowledgeQueries,
    moduleId,
    selectedKnowledge,
  }: RenderInputProps) => {
    const { systemVariables, getAllNodes, updateNodeValue } = useFlow();
    const [form] = Form.useForm();

    const variables = useMemo(() => {
      const templateVariables =
        getAllNodes()
          .find((node) => node.data.flowType === FlowNodeTypeEnum.templateParam)
          ?.data?.inputs.map((item) => ({
            key: item.key,
            label: item.label,
            isTemplateVariable: true,
          })) || [];
      const globalVariablesModule: Pick<VariableItemType, "key" | "label">[] =
        getAllNodes()
          .find(
            (node) => node.data.flowType === FlowNodeTypeEnum.globalVariable
          )
          ?.data?.inputs.filter((input) => input.edit)
          .map((item) => ({ key: item.key, label: item.label })) || [];

      const moduleVariablesModule: Pick<VariableItemType, "key" | "label">[] =
        inputs
          ?.filter((input) => input.type === FlowNodeInputTypeEnum.target)
          .map((item) => ({
            key: item.key,
            label: item.description || item.label,
            self: true,
          })) || [];

      return [
        ...templateVariables,
        ...globalVariablesModule,
        ...moduleVariablesModule,
        ...(systemVariables || []),
      ];
    }, [getAllNodes, systemVariables, inputs]);

    useUpdateEffect(() => {
      form.setFieldValue("fields", []);
      updateNodeValue(moduleId, item.key, []);
    }, [selectedKnowledge]);

    const conditions = useMemo(() => {
      return (knowledgeQueries as KnowledgeField[])
        ?.filter((item) => item.needEmbedding)
        .map((item) => ({ label: item.fieldName, value: item.fieldName }));
    }, [inputs]);

    const containerRef = useRef<HTMLDivElement>(null);

    return (
      <div ref={containerRef}>
        <Form
          initialValues={{ fields: item.value }}
          form={form}
          layout="vertical"
          onValuesChange={(_, values) => {
            updateNodeValue(moduleId, item.key, values.fields);
          }}
        >
          <Form.List name="fields">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map(({ name, ...restField }) => {
                    return (
                      <Space
                        key={name}
                        style={{ width: "100%", display: "flex" }}
                        wrap={false}
                        align="baseline"
                        className={
                          styles["advance-knowledgebase-condition-item"]
                        }
                      >
                        <Form.Item {...restField} name={[name, "name"]}>
                          <Select
                            options={conditions}
                            getPopupContainer={(node) =>
                              containerRef.current || node
                            }
                            placeholder="请选择向量搜索条件"
                          />
                        </Form.Item>
                        <div style={{ maxWidth: 300 }}>
                          <Form.Item {...restField} name={[name, "value"]}>
                            <PromptEditor
                              h={32}
                              variables={variables}
                              singleLine
                              showOpenModal={false}
                              showResize={false}
                            />
                          </Form.Item>
                        </div>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    );
                  })}
                  <Form.Item>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          name: undefined,
                          value: undefined,
                        })
                      }
                    >
                      添加向量搜索条件
                    </Button>
                  </Form.Item>
                </>
              );
            }}
          </Form.List>
        </Form>
      </div>
    );
  }
);

export default function NodeAdvancedKnowledgeBase({
  data,
  selected,
}: NodeProps<FlowModuleItemType>) {
  const [knowledgeQueries, setKnowledgeQueries] = useState<KnowledgeField[]>(
    []
  );

  const selectedKnowledge = useMemo(() => {
    return data.inputs.find((item) => item.key === "selectedList")
      ?.value as string;
  }, [data]);

  async function fetchKnowledgeQueries(baseId: string) {
    const rsp = await getKnowledgeFieldsService(baseId);
    if (rsp.data) {
      setKnowledgeQueries(rsp.data);
    }
  }

  // async function addInputByKnowledge(baseId: string) {
  //   const rsp = await getKnowledgeFieldsService(baseId);
  //   if (rsp.data) {
  //     setKnowledgeQueries(rsp.data);
  //     rsp.data.forEach((field) => {
  //       addModuleInput(data.moduleId, {
  //         key: field.fieldName,
  //         label: field.fieldName,
  //         type: 'target',
  //         valueType: field.type === 'NUMBER' ? 'number' : 'string',
  //         edit: true,
  //         editField: {
  //           required: true,
  //           inputType: false,
  //         },
  //       });
  //     });
  //   }
  // }

  useEffect(() => {
    // data.inputs.forEach((input) => {
    //   if (input.edit) {
    //     deleteModuleInput(data.moduleId, input.key);
    //     delEdgeByTargetHandle(data.moduleId, input.key);
    //   }
    // });
    if (selectedKnowledge) {
      fetchKnowledgeQueries(selectedKnowledge);
      // addInputByKnowledge(selectedKnowledge as string);
    }
  }, [selectedKnowledge]);

  return (
    <NodeCard {...data} selected={selected} minWidth={600}>
      <Divider text="输入" />
      <div className={styles.content}>
        <RenderInput
          // searchParam 兜底干掉 label 兼容原来的逻辑
          flowInputList={(data.inputs ?? []).map((input) =>
            input.key === "searchParam"
              ? {
                  ...input,
                  label: "",
                }
              : input
          )}
          moduleId={data.moduleId}
          CustomComponent={{
            searchParam: SearchParam,
            vectorSearch: VectorSearch,
          }}
          fieldProps={{ selectedKnowledge, knowledgeQueries }}
        />
      </div>
      <Divider text="输出" />
      <div className={styles.content}>
        <RenderOutput flowOutputList={data.outputs} moduleId={data.moduleId} />
      </div>
    </NodeCard>
  );
}
